import { ExclamationCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { Empty, Form, Input, Modal, UploadFile, UploadProps } from 'antd';
import Dragger from 'antd/es/upload/Dragger';
import React from 'react';
import classes from './NewVersionModal.module.scss';
import { availableIconFormat, documnentIcons } from 'helpers/documentIcons';
import UploadingComponent from 'components/UploadingComponent/UploadingComponent';
import { useTranslation } from 'react-i18next';
import { RcFile } from 'antd/es/upload';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setSelectedDocument, setViewType } from 'store/slices/dataDocumentsSlice';
import api from 'api';
import axios from 'axios';
import cn from 'classnames';
import { useNavigate } from 'react-router';
import { setDocumentToViewNull } from 'store/slices/viewerSlice';
import { setRoomAllChats } from 'store/slices/chatsSlice';
import { updateVersion } from 'store/reducers/documentsSocketsCreator';
import { getTheme } from 'helpers/getTheme';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';

type NewVersionModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  entity: {
    id?: string;
    name: string;
    extension?: string;
  };
  isNDA?: boolean;
  onSuccess?: () => void;
};

const NewVersionModal = ({isOpen, onCancel, entity, isNDA = false, onSuccess}: NewVersionModalProps) => {
  const [fileList, setFileList] = React.useState<UploadFile[]>([]);
  const [isUploadingVersion, setIsUploadingVersion] = React.useState(false);
  const [isNeedAgreementWarn, setIsNeedAgreementWarn] = React.useState(false);
  const [confirmUploadingModal, setConfirmUploadingModal] = React.useState(false);
  const [form] = Form.useForm();

  const { selectedDocument } = useAppSelector((store) => store.documents);
  const { roomAllChats } = useAppSelector((store) => store.chat);
  const { dataRoom } = useAppSelector((store) => store.dataRoom);
  const { messageApi } = useAppSelector((store) => store.windowState);

  const navigate = useNavigate();
  
  React.useEffect(() => {
    if (isNDA) {
      setIsNeedAgreementWarn(false);
    } else {
      setIsNeedAgreementWarn(selectedDocument?.agreement_status !== null);
    }
  }, [selectedDocument, isNDA]);

  const continueUploading = () => setIsNeedAgreementWarn(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const getExtension = () => {
    if (entity?.extension) return entity.extension;
    const parts = entity?.name?.split('.');
    return parts && parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  };
  const type = availableIconFormat.includes(getExtension()) ? getExtension() : 'default';
  const icon = type as keyof typeof documnentIcons;
  
  const onBeforeUpload = (file: RcFile) => {
    try {
      const parts = file.name.split('.');
      const extension = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';

      if (isNDA && !['pdf', 'docx', 'doc'].includes(extension)) {
        messageApi?.error({
          content: t('Documents.error.formatError'),
          key: 'formatError',
        });
        return false;
      }

      if (file.size > 500 * 1024 * 1024) {
        messageApi?.error({
          content: t('Documents.error.uploadDocumentSize'),
          key: 'uploadDocumentSize',
        });
        return false;
      }
      
      if (file.size === 0) {
        messageApi?.error(t('Documents.error.uploadDocumentSizeZero', {fileName: file.name}), 10);
        return false;
      }
      
      if (extension === 'exe' || extension === 'bin') {
        messageApi?.error({
          content: t('Documents.error.notSupported'),
          key: 'notSupported',
        });
        return false;
      }

      if (file.name.toLowerCase().replace(`.${extension}`, '').length > 150) {
        messageApi?.error({
          content: t('Documents.error.fileNameTooLong'),
          key: 'fileNameTooLong',
        });
        return false;
      }

      file.type && setFileList([file])
      return false;
    } catch (e) {
      console.log(e, 'uploading new version error');
    }
  };

  const iconRender = (file: UploadFile) => {
    const formatNDA = file?.name?.split('.')?.reverse()[0];  
    const icon = availableIconFormat.includes(formatNDA) ? formatNDA : 'default';
    return documnentIcons[icon as keyof typeof documnentIcons];
  };

  const props: UploadProps = {
    name: 'file',
    multiple: false,
    className: classes.dragger,
    onRemove: (file) => {
      setFileList(fileList.filter((currentFile)=> file.uid !== currentFile.uid))
    },
    iconRender: iconRender,
    beforeUpload: onBeforeUpload,
    listType: 'picture',
    // accept: '.doc,.xls,.xlsx,.docx,.pdf,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    accept: isNDA ? '.doc,.docx,.pdf' : undefined,
    fileList,
  };
  
  const sendNewVersion = async (comment: string) => {
    setIsUploadingVersion(true);
    try {
      const formData = new FormData();
      if (isNDA) {
        if (!dataRoom?.id) return;
        formData.append('room_id', dataRoom.id);
        formData.append('agreement_file', fileList[0] as RcFile);
        if (comment) {
          formData.append('comment', comment);
        }

        await api.uploadNDAVersion(formData);
        form.resetFields(['comment']);
        setFileList([]);
        messageApi?.success(t('Settings.terms.versions.uploadSuccess'));
        if (onSuccess) {
          onSuccess();
        }
        onCancel();
      } else {
        if (!entity.id) return;
        formData.append('document', fileList[0] as RcFile);
        formData.append('parent_version_id', entity.id);
        comment && formData.append('comment', comment);

        const response = await api.uploadDocumentVersion({ formData: formData });
        form.resetFields(['comment']);

        dispatch(updateVersion({data: response.data, entityId: entity!.id }));
        
        const targetChat = roomAllChats.find(chat => chat.entity_id === entity!.id);
        if (targetChat) {
          const chatsReplacedBasedOn = roomAllChats.map((chat) => chat.entity_id === entity!.id
            ? {
              ...chat,
              entity_id: response.data.id,
              entity_name: response.data.name,
              title: response.data.name,
            }
            : chat
          );
          dispatch(setRoomAllChats(chatsReplacedBasedOn));

        // Для удаления сообщение в подменяемом чате, чтобы при переходе в него подтянуть новые сообщение
        // const chatDataStore = JSON.parse(JSON.stringify(chatData)) as typeof chatData;
        // const chatId = targetChat.id || '';
        // if (chatDataStore[chatId]) chatDataStore[chatId].data = [];
        // dispatch(setChatData(chatDataStore));
        };
        
        messageApi?.success(t('Documents.details.versions.uploadVersionSuccessful'))
        onCancel();
        dispatch(setViewType('Table'));
        dispatch(setDocumentToViewNull());
        dispatch(setSelectedDocument(null));
        navigate(`${window.location.pathname}`);
      }
    } catch (e: any) {      
      if (e?.response?.data?.message === 'Room has namesakes documents'){
        messageApi?.error(t('Documents.error.dublicate'),5);
        return;
      } else if (e?.response?.data?.message === "Can't find parent version of the document") {
        messageApi?.error(t('Documents.details.versions.cantFindParent'),5);
        return;
      }
      const detail = axios.isAxiosError(e) ? e.response?.data?.detail : null;
      messageApi?.error(detail || (isNDA ? t('Settings.terms.versions.uploadError') : t('Documents.details.versions.uploadVersionError')));
    } finally {
      setIsUploadingVersion(false);
    }
  }
  const onFinish = (values: any) => {
    sendNewVersion(values.comment);
  };

  const modalTitle = (
    <div>
      <span>{isNDA ? t('Settings.terms.versions.uploadNew') : t('Documents.modals.newVersion.uploadNewVersion')}</span>
      <span className={classes.modalTitleIcon}>{documnentIcons[icon]}</span>
      <span>{entity?.name}</span>
    </div>
  );

  const okClick = () => isNDA ? setConfirmUploadingModal(true) : form.submit();
  
  const uploadNewNDA = () => {
    form.submit();
    setConfirmUploadingModal(false);
  };

  return (
    <Modal 
      width={'70%'}
      open={isOpen}
      title={modalTitle}
      centered={true}
      okText={isNeedAgreementWarn ? t('Documents.modals.newVersion.continueUploading') : t('Documents.modals.newVersion.uploadButton')}
      okButtonProps={{disabled: !isNeedAgreementWarn && (!fileList[0] || isUploadingVersion), loading: isUploadingVersion}}
      onCancel={onCancel}
      onOk={isNeedAgreementWarn ? continueUploading : okClick}
      className={cn(classes.modal, getTheme())}
    >
      <div className={cn(classes.info, isNeedAgreementWarn ? 'null' : classes.displayNone)}>
        <WarningOutlined className={classes.warningIcon}/>
        <div className={classes.text}>
        <p>{t('Documents.modals.newVersion.agreementWarn')}</p>
        </div>
      </div>
      
      <div className={cn(classes.uploadHidden, isNeedAgreementWarn ? classes.displayNone : classes.elementApear)}>
        <Dragger {...props}>
          <Empty rootClassName={classes.emptyElement} image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('Documents.modals.newVersion.clickOrDrop')} />
        </Dragger>
        <Form onFinish={onFinish} form={form} layout='vertical' style={{paddingTop: 10}}>
          <Form.Item
            label={<span style={{fontWeight: 600}}>{t('Documents.modals.newVersion.comment')}</span>}
            name='comment'
          >
            <Input.TextArea
              maxLength={500}
              showCount
              disabled={!fileList[0]}
              placeholder={t('Documents.modals.newVersion.enterComment')}
              autoSize={{ maxRows: 10, minRows: 4 }}
            />
          </Form.Item>
        </Form>
      </div>
      
      {isUploadingVersion && <UploadingComponent />}
      {confirmUploadingModal && (
        <React.Suspense fallback={<div />}>
          <ConfirmationModal
            isOpen={confirmUploadingModal}
            onCancel={() => setConfirmUploadingModal(false)}
            isLoading={isUploadingVersion}
            centered
            submit={uploadNewNDA}
            okText={t('Settings.terms.confirmModal.okButton')}
            text={<div className={classes.textModal}>{t('Settings.terms.confirmModal.textNDAUpdate')}</div>}
            title={<div className={classes.titleRow}>
              <ExclamationCircleOutlined className={classes.exclamationIcon}/>{t('Settings.terms.confirmModal.title')}
            </div>}
          />
        </React.Suspense>
      )}
    </Modal>
  );
}

export default NewVersionModal;
